import { useEffect, useState } from 'react';
import { KeyRound, UserRound } from 'lucide-react';
import AppIcon from '../components/AppIcon';
import { getErrorMessage } from '../api/errors';
import { useAuth } from '../context/AuthContext';

export default function Profile() {
  const { user, updateProfile, changePassword } = useAuth();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name);
    setPhone(user.phone ?? '');
  }, [user]);

  const handleProfileSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setProfileError('');
    setProfileMessage('');

    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();

    if (!trimmedName) {
      setProfileError('Ad soyad boş olamaz.');
      return;
    }

    if (trimmedPhone && (trimmedPhone.length < 7 || trimmedPhone.length > 30)) {
      setProfileError('Telefon numarası 7 ile 30 karakter arasında olmalıdır.');
      return;
    }

    setProfileSaving(true);
    try {
      await updateProfile({
        name: trimmedName,
        phone: trimmedPhone ? trimmedPhone : null,
      });
      setProfileMessage('Profil bilgileriniz güncellendi.');
    } catch (err) {
      setProfileError(getErrorMessage(err, 'Profil güncellenemedi.'));
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordError('');
    setPasswordMessage('');

    if (!currentPassword) {
      setPasswordError('Mevcut şifrenizi girin.');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Yeni şifre en az 6 karakter olmalıdır.');
      return;
    }

    if (newPassword === currentPassword) {
      setPasswordError('Yeni şifre mevcut şifreden farklı olmalıdır.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Yeni şifreler eşleşmiyor.');
      return;
    }

    setPasswordSaving(true);
    try {
      const result = await changePassword({ currentPassword, newPassword });
      setPasswordMessage(result.message);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(getErrorMessage(err, 'Şifre güncellenemedi.'));
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="container profile-page">
      <div className="page-header">
        <div>
          <h1>Profilim</h1>
          <p>Hesap bilgilerinizi ve şifrenizi yönetin</p>
        </div>
      </div>

      <div className="profile-grid">
        <section className="profile-card">
          <div className="profile-card-header">
            <span className="profile-card-icon">
              <AppIcon icon={UserRound} size={24} />
            </span>
            <div>
              <h2>Profil Bilgileri</h2>
              <p>Ad, telefon ve e-posta bilgileriniz</p>
            </div>
          </div>

          {profileMessage && <div className="auth-success">{profileMessage}</div>}
          {profileError && <div className="auth-error">{profileError}</div>}

          <form className="profile-form" onSubmit={(event) => void handleProfileSubmit(event)}>
            <div className="form-group">
              <label htmlFor="profile-name">Ad Soyad</label>
              <input
                id="profile-name"
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="name"
                maxLength={200}
                required
                disabled={profileSaving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="profile-phone">Telefon</label>
              <input
                id="profile-phone"
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                autoComplete="tel"
                placeholder="+905551234567"
                maxLength={30}
                disabled={profileSaving}
              />
              <span className="form-hint">Boş bırakırsanız kayıtlı telefon silinir.</span>
            </div>

            <div className="form-group">
              <label htmlFor="profile-email">E-posta</label>
              <input
                id="profile-email"
                type="email"
                value={user?.email ?? ''}
                readOnly
                disabled
                aria-describedby="profile-email-hint"
              />
              <span id="profile-email-hint" className="form-hint">
                E-posta adresi bu ekrandan değiştirilemez.
              </span>
            </div>

            <button type="submit" className="btn btn-primary" disabled={profileSaving}>
              {profileSaving ? 'Kaydediliyor...' : 'Profili Kaydet'}
            </button>
          </form>
        </section>

        <section className="profile-card">
          <div className="profile-card-header">
            <span className="profile-card-icon">
              <AppIcon icon={KeyRound} size={24} />
            </span>
            <div>
              <h2>Şifre Değiştir</h2>
              <p>Mevcut şifrenizi doğrulayarak yeni şifre belirleyin</p>
            </div>
          </div>

          {passwordMessage && <div className="auth-success">{passwordMessage}</div>}
          {passwordError && <div className="auth-error">{passwordError}</div>}

          <form className="profile-form" onSubmit={(event) => void handlePasswordSubmit(event)}>
            <div className="form-group">
              <label htmlFor="current-password">Mevcut Şifre</label>
              <input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
                required
                disabled={passwordSaving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="new-password">Yeni Şifre</label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                minLength={6}
                required
                disabled={passwordSaving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirm-password">Yeni Şifre (Tekrar)</label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                minLength={6}
                required
                disabled={passwordSaving}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={passwordSaving}>
              {passwordSaving ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
